// src/hooks/useReclamos.js
import { useState, useEffect, useCallback } from "react";
import apiService from "../services/api.service";
import imageService from "../services/image.service";

export const useReclamos = (fetchAll = false) => {
  const [reclamos, setReclamos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReclamos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = fetchAll 
        ? await apiService.getAllReclamos()
        : await apiService.getMyReclamos();
            
      const reclamosConImagenes = data.map(reclamo => ({
        ...reclamo,
        imagenes: reclamo.imagenes || []
      }));
      
      setReclamos(reclamosConImagenes);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching reclamos:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchAll]);

  useEffect(() => {
    fetchReclamos();
  }, [fetchReclamos]);

  const createReclamo = async (reclamoData, images = []) => {
    setError(null);
    let uploadedImages = [];
    
    try {
      // Validar y filtrar imágenes
      const validImages = Array.isArray(images) ? images.filter(img => img instanceof File) : [];

      // 1. Si hay imágenes, subirlas primero a Firebase Storage
      if (validImages.length > 0) {
        console.log('Subiendo imágenes a Firebase Storage...');
        uploadedImages = await imageService.uploadImages(validImages);
        console.log('Imágenes subidas exitosamente:', uploadedImages);
      } else {
        console.log('No hay imágenes para subir');
      }

      // 2. Crear el reclamo en la base de datos
      const nuevoReclamo = await apiService.createReclamo(reclamoData);
      console.log('Reclamo creado exitosamente:', nuevoReclamo);

      // 3. Si hay imágenes, crear los registros en la base de datos
      if (uploadedImages.length > 0) {
        const imageRecords = await imageService.createImageRecords(uploadedImages, nuevoReclamo.id);
        console.log('Registros de imágenes creados:', imageRecords);
        
        // Asignar las imágenes al reclamo
        nuevoReclamo.imagenes = imageRecords;
      } else {
        nuevoReclamo.imagenes = [];
      }

      // 4. Actualizar el estado local
      setReclamos(prev => [nuevoReclamo, ...prev]);
      
      return nuevoReclamo;
    } catch (err) {
      console.error('ERROR EN CREACIÓN DE RECLAMO:', err);
      
      // Si hubo error después de subir imágenes, limpiar
      if (uploadedImages.length > 0) {
        console.log('Limpiando imágenes subidas debido al error...');
        try {
          await imageService.deleteImages(uploadedImages);
          console.log('Imágenes limpiadas exitosamente');
        } catch (cleanupError) {
          console.error('Error en limpieza de imágenes:', cleanupError);
        }
      }
      
      setError(err.message);
      throw err;
    }
  };

  const updateEstado = async (reclamoId, nuevoEstado) => {
    // Optimistic update
    const previousReclamos = [...reclamos];
    setReclamos(prev => 
      prev.map(r => r.id === reclamoId ? { ...r, estado: nuevoEstado } : r)
    );
    
    try {
      const updated = await apiService.updateReclamoEstado(reclamoId, nuevoEstado);
      setReclamos(prev =>
        prev.map(r => r.id === reclamoId ? updated : r)
      );
      return updated;
    } catch (err) {
      // Revert on error
      setReclamos(previousReclamos);
      throw err;
    }
  };

  const updatePrioridad = async (reclamoId, nuevaPrioridad) => {
    // Optimistic update
    const previousReclamos = [...reclamos];
    setReclamos(prev =>
      prev.map(r => r.id === reclamoId ? { ...r, prioridad: nuevaPrioridad } : r)
    );

    try {
      const updated = await apiService.updateReclamoPrioridad(reclamoId, nuevaPrioridad);
      setReclamos(prev =>
        prev.map(r => r.id === reclamoId ? updated : r)
      );
      return updated;
    } catch (err) {
      // Revert on error
      setReclamos(previousReclamos);
      throw err;
    }
  };

  const deleteReclamo = async (reclamoId) => {
    setError(null);
    try {
      const reclamo = reclamos.find(r => r.id === reclamoId);
      if (!reclamo) throw new Error('Reclamo no encontrado');
      
      const imagenesDelReclamo = reclamo.imagenes || [];
      
      // 1. Eliminar imágenes si existen
      if (imagenesDelReclamo.length > 0) {
        // Eliminar registros de la base de datos
        await Promise.allSettled(
          imagenesDelReclamo.map(imagen => 
            apiService.deleteImagen(imagen.id).catch(err => {
              console.warn(`Error eliminando registro de imagen ${imagen.id}:`, err);
            })
          )
        );
        
        console.log('Eliminando archivos de Firebase Storage...');
        // Eliminar archivos de Firebase Storage
        await imageService.deleteImages(imagenesDelReclamo);
      } else {
        console.log('No hay imágenes para eliminar');
      }

      // 2. Eliminar el reclamo principal
      await apiService.deleteReclamo(reclamoId);

      // 3. Actualizar estado local
      setReclamos(prev => prev.filter(r => r.id !== reclamoId));
      
    } catch (err) {
      console.error('ERROR ELIMINANDO RECLAMO:', err);
      setError(err.message);
      throw err;
    }
  };

  return {
    reclamos,
    loading,
    error,
    fetchReclamos,
    createReclamo,
    updateEstado,
    deleteReclamo,
    updatePrioridad
  };
};